import { organizationSchema } from "@/features/seo";
import { getCanonicalBase } from "@/config/brands/brand-tech";

type Breadcrumb = { name: string; url: string };

type SeoProps = {
  route?: string;
  jsonLd?: any; // Page-specific schema
  breadcrumbs?: Breadcrumb[]; // For breadcrumb schema
  graph?: any[]; // Array of schemas for the @graph
};

/**
 * Seo Component
 * Handles the generation of Schema.org JSON-LD structured data.
 * NOTE: For title, description, and other meta tags, use Next.js native Metadata API 
 * in the page level (export const metadata or generateMetadata).
 */
export function Seo({ breadcrumbs, graph, jsonLd }: SeoProps) {
  // Build the schema graph
  const schemaGraph = graph ? [...graph] : [];

  // Always include organization if not explicitly excluded or already present
  if (!schemaGraph.some(s => s["@type"] === "Organization")) {
    schemaGraph.push(organizationSchema);
  }

  // Add breadcrumbs to the graph if provided
  if (breadcrumbs) {
    const siteUrl = getCanonicalBase();
    schemaGraph.push({
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.name,
        item: crumb.url.startsWith("http") ? crumb.url : `${siteUrl}${crumb.url}`,
      })),
    });
  }

  // Add page-specific jsonLd to the graph if provided
  if (jsonLd) {
    if (Array.isArray(jsonLd)) {
      schemaGraph.push(...jsonLd);
    } else {
      schemaGraph.push(jsonLd);
    }
  }

  const finalJsonLd = {
    "@context": "https://schema.org",
    "@graph": schemaGraph,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(finalJsonLd) }}
    />
  );
}
